import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "../../components/ui/Modal";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Alert } from "../../components/ui/Alert";
import { Spinner } from "../../components/ui/Spinner";
import { useApi } from "../../context/ApiContext";
import SearchableDropdown from "../../components/SearchableDropdown";
import { CURRENCIES } from "../../utils/countryCurrencyData";

interface CurrencyFormProps {
 isOpen: boolean;
 rateId?: string | null;
 onClose: () => void;
 onSuccess: () => void;
}

export const CurrencyForm: React.FC<CurrencyFormProps> = ({
 isOpen,
 rateId,
 onClose,
 onSuccess,
}) => {
 const { t } = useTranslation();
 const { api } = useApi();
 const [loading, setLoading] = useState(false);
 const [fetchingRate, setFetchingRate] = useState(false);
 const isFetchingRateRef = useRef(false);
 const [error, setError] = useState<string | null>(null);
 const [liveRate, setLiveRate] = useState<number | null>(null);
 const [formData, setFormData] = useState({
  fromCurrency: "",
  toCurrency: "",
  rate: 0,
  source: "manual",
 });
 const formDataRef = useRef(formData);
 const hasLoadedInitialDataRef = useRef(false);

 // Keep ref in sync with state
 useEffect(() => {
  formDataRef.current = formData;
 }, [formData]);

 const fetchRate = useCallback(async () => {
  try {
   setLoading(true);
   const response = await api.get(`/currencies/admin/${rateId}`);
   const rate = response.data;
   if (rate) {
    setFormData({
     fromCurrency: rate.fromCurrency || "",
     toCurrency: rate.toCurrency || "",
     rate: rate.rate || 0,
     source: rate.source || "manual",
    });
   }
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.currencies.errors.fetch"));
  } finally {
   setLoading(false);
  }
 }, [api, rateId, t]);

 const fetchLiveRate = useCallback(async () => {
  const currentFormData = formDataRef.current;
  
  if (!currentFormData.fromCurrency || !currentFormData.toCurrency || currentFormData.source === "manual") {
   return;
  }

  try {
   setFetchingRate(true);
   isFetchingRateRef.current = true;
   setError(null);
   // Call backend to fetch live rate from the selected API
   const response = await api.get(
    `/currencies/admin/fetch-rate?from=${currentFormData.fromCurrency.toUpperCase()}&to=${currentFormData.toCurrency.toUpperCase()}&source=${currentFormData.source}`
   );
   if (response.data && response.data.rate !== undefined && response.data.rate !== null) {
    const fetchedRate = Number(response.data.rate);
    if (!isNaN(fetchedRate)) {
     setLiveRate(fetchedRate);
     setFormData((prev) => {
      const updated = { ...prev, rate: fetchedRate };
      formDataRef.current = updated;
      return updated;
     });
    }
   }
  } catch (err: any) {
   console.error("Failed to fetch live rate:", err);
   setError(err.response?.data?.error || "Failed to fetch live exchange rate");
  } finally {
   setFetchingRate(false);
   isFetchingRateRef.current = false;
  }
 }, [api, t]);

 useEffect(() => {
  if (isOpen && rateId && !hasLoadedInitialDataRef.current) {
   hasLoadedInitialDataRef.current = true;
   fetchRate();
  } else if (isOpen && !rateId) {
   hasLoadedInitialDataRef.current = false;
   setFormData({
    fromCurrency: "",
    toCurrency: "",
    rate: 0,
    source: "manual",
   });
   setLiveRate(null);
   setError(null);
  } else if (!isOpen) {
   // Reset the flag when modal closes
   hasLoadedInitialDataRef.current = false;
  }
 }, [isOpen, rateId, fetchRate]);

 const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  // Prevent form submission if we're currently fetching a rate (button was clicked)
  if (isFetchingRateRef.current) {
   return;
  }
  try {
   setLoading(true);
   setError(null);

   const payload = {
    ...formData,
    fromCurrency: formData.fromCurrency.toUpperCase(),
    toCurrency: formData.toCurrency.toUpperCase(),
    // Only use liveRate if source is not manual (API sources)
    rate: formData.source !== "manual" && liveRate !== null ? liveRate : formData.rate,
   };

   if (rateId) {
    await api.put(`/currencies/admin/${rateId}`, payload);
   } else {
    await api.post("/currencies/admin", payload);
   }

   onSuccess();
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.currencies.errors.save"));
  } finally {
   setLoading(false);
  }
 };

 return (
  <Modal
   isOpen={isOpen}
   onClose={onClose}
   title={rateId ? t("admin.currencies.editRate") : t("admin.currencies.newRate")}
  >
   <form 
    onSubmit={handleSubmit} 
    className="space-y-4"
    onKeyDown={(e) => {
     // Prevent Enter key from submitting form if we're fetching a rate
     if (e.key === 'Enter' && isFetchingRateRef.current) {
      e.preventDefault();
      e.stopPropagation();
     }
    }}
   >
    {error && <Alert variant="error">{error}</Alert>}

    <div className="grid grid-cols-2 gap-4">
     <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
       {t("admin.currencies.fromCurrencyIso")} <span className="text-red-500">*</span>
      </label>
      <SearchableDropdown
       fetchOptions={async (search: string) => {
        const searchLower = search.toLowerCase();
        return CURRENCIES.filter((currency) =>
         currency.code.toLowerCase().includes(searchLower) ||
         currency.name.toLowerCase().includes(searchLower) ||
         currency.symbol.toLowerCase().includes(searchLower)
        )
         .map((currency) => ({
          _id: currency.code,
          label: `${currency.code} - ${currency.name} (${currency.symbol})`,
         }))
         .slice(0, 50); // Limit results for performance
       }}
       placeholder={t("admin.currencies.fromPlaceholder") || "USD, EUR..."}
       value={formData.fromCurrency}
       getDisplayLabel={(val: string) => {
        const currency = CURRENCIES.find((c) => c.code === val);
        return currency ? `${currency.code} - ${currency.name}` : val;
       }}
       onSelect={(option) => {
        const newFromCurrency = option._id.toUpperCase();
        // Clear liveRate when currency changes (liveRate is specific to currency pair)
        if (newFromCurrency !== formData.fromCurrency) {
         setLiveRate(null);
        }
        setFormData({
         ...formData,
         fromCurrency: newFromCurrency,
        });
       }}
      />
     </div>

     <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
       {t("admin.currencies.toCurrencyIso")} <span className="text-red-500">*</span>
      </label>
      <SearchableDropdown
       fetchOptions={async (search: string) => {
        const searchLower = search.toLowerCase();
        return CURRENCIES.filter((currency) =>
         currency.code.toLowerCase().includes(searchLower) ||
         currency.name.toLowerCase().includes(searchLower) ||
         currency.symbol.toLowerCase().includes(searchLower)
        )
         .map((currency) => ({
          _id: currency.code,
          label: `${currency.code} - ${currency.name} (${currency.symbol})`,
         }))
         .slice(0, 50); // Limit results for performance
       }}
       placeholder={t("admin.currencies.toPlaceholder") || "EUR, GBP..."}
       value={formData.toCurrency}
       getDisplayLabel={(val: string) => {
        const currency = CURRENCIES.find((c) => c.code === val);
        return currency ? `${currency.code} - ${currency.name}` : val;
       }}
       onSelect={(option) => {
        const newToCurrency = option._id.toUpperCase();
        // Clear liveRate when currency changes (liveRate is specific to currency pair)
        if (newToCurrency !== formData.toCurrency) {
         setLiveRate(null);
        }
        setFormData({
         ...formData,
         toCurrency: newToCurrency,
        });
       }}
      />
     </div>
    </div>

    <div>
     <label className="block text-sm font-medium text-gray-700 mb-2">
      {t("admin.currencies.source")}
     </label>
     <SearchableDropdown
      fetchOptions={async (search: string) => {
       const sources = [
        { _id: "manual", label: t("admin.currencies.sourceManual") },
        { _id: "exchangerate-api", label: t("admin.currencies.sourceExchangeRate") },
        { _id: "fixer", label: t("admin.currencies.sourceFixer") },
        { _id: "currencylayer", label: t("admin.currencies.sourceCurrencyLayer") },
       ];
       return sources.filter((source) =>
        source.label.toLowerCase().includes(search.toLowerCase())
       );
      }}
      placeholder={t("admin.currencies.searchSource")}
      value={formData.source}
      getDisplayLabel={(val: string) => {
       const sourceMap: Record<string, string> = {
        manual: t("admin.currencies.sourceManual"),
        "exchangerate-api": t("admin.currencies.sourceExchangeRate"),
        fixer: t("admin.currencies.sourceFixer"),
        currencylayer: t("admin.currencies.sourceCurrencyLayer"),
       };
       return sourceMap[val] || val;
      }}
      onSelect={(option) => {
       const newSource = option._id;
       setFormData({ ...formData, source: newSource });
       // Clear liveRate when switching to manual so user can edit manually
       if (newSource === "manual") {
        setLiveRate(null);
       }
      }}
     />
    </div>

    <div>
     <div className="flex items-center justify-between mb-2">
      <div className="flex-1">
       <label className="block text-sm font-medium text-gray-700 mb-1">
        {t("admin.currencies.exchangeRate")}
       </label>
       {formData.fromCurrency && formData.toCurrency && (
        <div className="flex items-center gap-2 text-sm text-gray-600">
         <span className="px-2 inline-flex text-xs leading-5 font-semibold bg-blue-100 text-blue-800">
          {formData.fromCurrency}
         </span>
         <span className="font-medium">=</span>
         <span className="font-semibold text-gray-900">{formData.rate > 0 ? formData.rate.toFixed(4) : "0.0000"}</span>
         <span className="font-medium">×</span>
         <span className="px-2 inline-flex text-xs leading-5 font-semibold bg-green-100 text-green-800">
          {formData.toCurrency}
         </span>
        </div>
       )}
      </div>
      {formData.source !== "manual" && formData.fromCurrency && formData.toCurrency && (
       <button
        type="button"
        className="px-3 py-1.5 text-sm font-medium transition-all duration-200 border-2 border-store-primary text-store-primary hover:bg-store-background active:bg-store-secondary active:text-white focus:outline-none focus:ring-2 focus:ring-store-secondary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={(e) => {
         // CRITICAL: Prevent form submission immediately
         e.preventDefault();
         e.stopPropagation();
         e.nativeEvent.stopImmediatePropagation();
         // Set flag immediately to prevent any form submission
         isFetchingRateRef.current = true;
         fetchLiveRate();
         return false;
        }}
        disabled={fetchingRate}
       >
        {fetchingRate ? <Spinner size="sm" /> : t("admin.currencies.fetchLiveRate")}
       </button>
      )}
     </div>
     <Input
      type="number"
      step="0.0001"
      min="0"
      required
      disabled={formData.source !== "manual"}
      value={formData.rate}
      onChange={(e) => {
       // Only allow editing when source is manual
       if (formData.source !== "manual") {
        return;
       }
       const newValue = e.target.value === "" ? 0 : parseFloat(e.target.value) || 0;
       setFormData((prev) => {
        const updated = { ...prev, rate: newValue };
        formDataRef.current = updated;
        return updated;
       });
      }}
      placeholder="1.2345"
     />
     {formData.source !== "manual" && liveRate !== null && liveRate !== formData.rate && (
      <p className="text-sm text-green-600 mt-1">
       {t("admin.currencies.liveRateFrom", { source: formData.source, rate: liveRate.toFixed(4) })}
      </p>
     )}
     {formData.source !== "manual" && (
      <p className="text-xs text-gray-500 mt-1">
       {t("admin.currencies.fetchLiveRateHint", { source: formData.source })}
      </p>
     )}
    </div>

    <div className="flex gap-2 justify-end pt-4 border-t">
     <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
      {t("common.cancel")}
     </Button>
     <Button type="submit" disabled={loading}>
      {loading ? <Spinner size="sm" /> : rateId ? t("common.update") : t("common.create")}
     </Button>
    </div>
   </form>
  </Modal>
 );
};

